<template>

    <div class="section mt-1">
        <div class="sneha-filter-row">
            <select class="sneha-filter-select mal-text" v-model="program_id" @change="onProgramChange">
                <option value="">എല്ലാ പദ്ധതികളും</option>
                <option v-for="p in programs" :key="p.id" :value="p.id">{{ p.title }}</option>
            </select>
            <select v-if="isStateAdmin" class="sneha-filter-select mal-text" v-model="district_id" @change="loadApplications()">
                <option value="">എല്ലാ ജില്ലകളും</option>
                <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
        </div>
    </div>

    <!-- Question/answer filters — only meaningful once a specific programme is picked above,
         since each programme has its own distinct set of `filterable`-flagged questions.
         Answers are multi-select: picking more than one for the same question OR-matches
         ("District is X OR Y"); the (up to 3) different questions still AND together. -->
    <div class="section mt-1" v-if="program_id && filterableQuestions.length">
        <div class="sneha-qfilter-box">
            <div class="sneha-qfilter-item" v-for="(f, idx) in questionFilters" :key="idx">
                <div class="sneha-qfilter-row">
                    <select class="sneha-filter-select mal-text sneha-qfilter-q" v-model="f.key" @change="onQuestionFilterKeyChange(idx)">
                        <option value="">ചോദ്യം തിരഞ്ഞെടുക്കുക</option>
                        <option v-for="q in filterableQuestions" :key="q.key" :value="q.key" :disabled="isQuestionFilterUsed(q.key, idx)">{{ q.label }}</option>
                    </select>
                    <button type="button" class="sneha-qfilter-remove" @click="removeQuestionFilter(idx)">
                        <ion-icon name="close-outline"></ion-icon>
                    </button>
                </div>
                <button type="button" v-if="f.key" class="sneha-filter-select mal-text sneha-qfilter-a-btn" @click="toggleAnswerPicker(idx)">
                    <span class="sneha-qfilter-a-text">{{ f.values.length ? f.values.join(', ') : 'ഉത്തരം തിരഞ്ഞെടുക്കുക' }}</span>
                    <ion-icon name="chevron-down-outline"></ion-icon>
                </button>
                <div class="sneha-qfilter-answers" v-if="f.key && openAnswerIdx === idx">
                    <label class="sneha-qfilter-answer-opt mal-text" v-for="opt in questionFilterOptionsFor(f.key)" :key="opt">
                        <input type="checkbox" :checked="f.values.includes(opt)" @change="toggleFilterValue(idx, opt)">
                        <span>{{ opt }}</span>
                    </label>
                    <p class="sneha-qfilter-answer-empty mal-text" v-if="!questionFilterOptionsFor(f.key).length">
                        ഈ ചോദ്യത്തിന് ഇതുവരെ ഉത്തരങ്ങളൊന്നും ലഭ്യമല്ല.
                    </p>
                    <button type="button" class="sneha-qfilter-answer-done" @click="openAnswerIdx = null">Done</button>
                </div>
            </div>
            <button type="button" class="sneha-qfilter-add" v-if="questionFilters.length < 3 && questionFilters.length < filterableQuestions.length" @click="addQuestionFilter">
                <ion-icon name="add-outline"></ion-icon>
                <span class="mal-text">ചോദ്യം ഫിൽട്ടർ ചേർക്കുക</span>
            </button>
            <div class="sneha-qfilter-actions" v-if="questionFilters.length">
                <button type="button" class="sneha-qfilter-clear-btn" @click="clearQuestionFilters">Clear</button>
                <button type="button" class="sneha-qfilter-apply-btn" @click="loadApplications()">Filter</button>
            </div>
        </div>
    </div>

    <!-- Priority Questions — a personal, per-approver preference (not shared): which of THIS
         programme's questions to show FIRST on every application's detail page, the rest
         following after. Unlike the Filter box above, this pulls from EVERY question — Extra
         Fields, Verification Questions, and the relief-family defaults — not just the ones an
         admin flagged Filterable, since here the approver is choosing what to see, not
         narrowing a search. -->
    <div class="section mt-1" v-if="program_id && priorityQuestionOptions.length">
        <div class="sneha-qfilter-box">
            <button type="button" class="sneha-priority-toggle" @click="priorityOpen = !priorityOpen">
                <span class="mal-text">മുൻഗണനാ ചോദ്യങ്ങൾ<span v-if="priorityQuestions.length"> ({{ priorityQuestions.length }})</span></span>
                <ion-icon :name="priorityOpen ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
            </button>
            <div class="sneha-qfilter-answers" v-if="priorityOpen">
                <p class="sneha-qfilter-answer-empty mal-text">
                    ഓരോ അപേക്ഷയിലും ഈ ചോദ്യങ്ങൾ ആദ്യം കാണിക്കും, ബാക്കിയുള്ളവ പിന്നീട്.
                </p>
                <label class="sneha-qfilter-answer-opt mal-text" v-for="q in priorityQuestionOptions" :key="q.key">
                    <input type="checkbox" :checked="priorityQuestions.includes(q.key)" @change="togglePriorityQuestion(q.key)">
                    <span>{{ q.label }}</span>
                </label>
                <button type="button" class="sneha-qfilter-answer-done" @click="savePriorityQuestions">Save</button>
            </div>
        </div>
    </div>

    <div class="section mt-1" v-if="isStateAdmin">
        <button type="button" class="sneha-export-btn" @click="exportExcel" :disabled="excelDownloading">
            <ion-icon name="download-outline"></ion-icon>
            <span>{{ excelDownloading ? 'എക്സ്പോർട്ട് ചെയ്യുന്നു...' : 'Export Excel' }}</span>
        </button>
    </div>

    <div class="section mt-2">
        <div class="sneha-count-grid">
            <div class="sneha-count-tile is-completed" :class="{ 'is-active': status_bucket === 'completed' }" @click="toggleBucket('completed')">
                <ion-icon name="checkmark-circle-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.completed }}</span>
                <span class="sneha-count-label mal-text">Completed</span>
            </div>
            <div class="sneha-count-tile is-going-on" :class="{ 'is-active': status_bucket === 'going_on' }" @click="toggleBucket('going_on')">
                <ion-icon name="time-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.going_on }}</span>
                <span class="sneha-count-label mal-text">Going On</span>
            </div>
            <div class="sneha-count-tile is-not-started" :class="{ 'is-active': status_bucket === 'not_started' }" @click="toggleBucket('not_started')">
                <ion-icon name="ellipse-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.not_started }}</span>
                <span class="sneha-count-label mal-text">Not Started</span>
            </div>
            <div class="sneha-count-tile is-vo-not-assigned" :class="{ 'is-active': status_bucket === 'vo_not_assigned' }" @click="toggleBucket('vo_not_assigned')">
                <ion-icon name="alert-circle-outline" class="sneha-count-icon"></ion-icon>
                <span class="sneha-count-num">{{ counts.vo_not_assigned }}</span>
                <span class="sneha-count-label mal-text">VO Not Assigned</span>
            </div>
        </div>
    </div>

    <div class="section text-center" v-if="loading">
        <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div class="section pt-2" v-else>
        <p class="sneha-page-note mal-text" v-if="applications.length">
            അപേക്ഷകൾ ({{ applications.length }})
        </p>

        <div v-if="!applications.length" class="sneha-empty-apps mal-text">
            ഇതുവരെ അപേക്ഷകൾ ഇല്ല.
        </div>

        <div v-else class="sneha-app-list">
            <div v-for="app in applications" :key="app.id" class="sneha-app-item" :class="{ 'is-open': isAppExpanded(app.id) }">
                <div class="sneha-app-summary" @click="toggleAppExpanded(app.id)">
                    <div class="sneha-app-main">
                        <span class="sneha-app-name mal-text">{{ app.name }}</span>
                        <span v-if="app.application_number" class="sneha-app-number">{{ app.application_number }}</span>
                        <div class="sneha-app-summary-badges">
                            <span class="badge" :class="statusBadgeClass(app.verification_status)">
                                {{ statusLabel(app.verification_status) }}
                            </span>
                            <span v-if="app.mark_score !== null && app.mark_score !== undefined" class="badge sneha-mark-badge">
                                <ion-icon name="star-outline"></ion-icon> {{ app.mark_score }}<span v-if="app.mark_max">/{{ app.mark_max }}</span>
                            </span>
                        </div>
                    </div>
                    <ion-icon name="chevron-down-outline" class="sneha-app-chevron"></ion-icon>
                </div>

                <div class="sneha-app-details" v-if="isAppExpanded(app.id)">
                    <div class="d-flex justify-content-between align-items-center gap-2">
                        <span class="sneha-app-percent" :class="percentClass(app.completion_percent)">{{ app.completion_percent }}% പൂർത്തിയായി</span>
                    </div>
                    <div class="sneha-app-progress-track">
                        <div class="sneha-app-progress-fill" :class="percentClass(app.completion_percent)"
                            :style="{ width: app.completion_percent + '%' }"></div>
                    </div>
                    <span class="sneha-app-program mal-text">{{ app.program_title }}</span>
                    <span class="sneha-app-meta mal-text">{{ app.unit_name }}<span v-if="app.zone_name"> · {{ app.zone_name }}</span><span v-if="app.district_name"> · {{ app.district_name }}</span></span>
                    <span class="sneha-app-meta mal-text" v-if="app.created_at">തീയതി: {{ app.created_at }}</span>
                    <span v-if="app.interview_status" class="badge mt-1 align-self-start sneha-interview-badge">
                        <ion-icon name="people-circle-outline"></ion-icon> Added to Interview List
                    </span>
                    <div class="sneha-vo-box mal-text" v-if="app.vo_name">
                        <ion-icon name="shield-checkmark-outline"></ion-icon>
                        <span>{{ app.vo_name }}<span v-if="app.vo_district_name"> · {{ app.vo_district_name }}</span><span v-if="app.vo_zone_name"> · {{ app.vo_zone_name }}</span><span v-if="app.vo_mobile"> · {{ app.vo_mobile }}</span></span>
                    </div>
                    <div class="sneha-vo-box sneha-vo-box--none mal-text" v-else>
                        <ion-icon name="alert-circle-outline"></ion-icon>
                        <span>Verification Officer നിയോഗിച്ചിട്ടില്ല</span>
                    </div>
                    <router-link :to="applicationDetailPath(app.id)" class="sneha-app-view-btn">
                        <ion-icon name="eye-outline"></ion-icon>
                        <span>View</span>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UserService from "@/services/user.service";

export default {
    name: "SnehaSparshamApplicationStatusReport",
    data() {
        return {
            loading: true,
            applications: [],
            programs: [],
            districts: [],
            program_id: "",
            district_id: "",
            status_bucket: "",
            counts: { completed: 0, going_on: 0, not_started: 0, vo_not_assigned: 0 },
            excelDownloading: false,
            // Question/answer filters — up to 3 {key, values: []} rows, only shown once a
            // specific programme is picked (each programme has its own `filterable`-flagged
            // questions). Multiple values per question OR-match; the rows themselves AND.
            filterableQuestions: [],
            questionFilters: [],
            // Which filter row's answer checklist is currently expanded (null = none).
            openAnswerIdx: null,
            // Priority Questions — this approver's own saved preference for which questions
            // show first on every application's detail page (rest follow after). The picker
            // pool (priorityQuestionOptions) is EVERY question for the programme, unlike
            // filterableQuestions above which is admin-curated.
            priorityQuestions: [],
            priorityQuestionOptions: [],
            priorityOpen: false,
            // Which application cards are expanded to show full details — collapsed (id
            // absent/false) by default so the list stays compact/scannable on mobile.
            expandedApps: {},
        };
    },
    computed: {
        isStateAdmin() {
            return parseInt(this.$route.params.sub_level, 10) === 1;
        },
        // Same screen serves both entry points: the plain status report (everything in scope)
        // and "Assigned Applications" (?assigned=1 — just this approver's distributed batch).
        assignedOnly() {
            return String(this.$route.query.assigned || '') === '1';
        },
        // Only the fully-picked rows (a question AND at least one answer chosen) count as
        // active — a half-filled row shouldn't narrow the list yet.
        activeQuestionFiltersJson() {
            const active = this.questionFilters.filter((f) => f.key && f.values.length)
                .map((f) => ({ key: f.key, values: f.values }));
            return active.length ? JSON.stringify(active) : null;
        },
    },
    mounted() {
        this.$root.updateParent(this.assignedOnly ? 'Assigned Applications' : 'അപേക്ഷ സ്റ്റാറ്റസ് റിപ്പോർട്ട്');
        // Restore filters from the URL — this page fully remounts (no keep-alive in this app)
        // every time you come back from an application's detail page, so without this the
        // programme/district/bucket/question-filter selections would silently reset each time.
        this.restoreFiltersFromQuery();
        if (this.program_id) {
            this.loadFilterableQuestions();
            this.loadPriorityQuestionOptions();
            this.loadPriorityQuestions();
        }
        this.loadApplications();
    },
    methods: {
        restoreFiltersFromQuery() {
            const q = this.$route.query;
            if (q.program_id) this.program_id = q.program_id;
            if (q.district_id) this.district_id = q.district_id;
            if (q.status_bucket) this.status_bucket = q.status_bucket;
            if (q.question_filters) {
                try {
                    const parsed = JSON.parse(q.question_filters);
                    if (Array.isArray(parsed)) {
                        this.questionFilters = parsed.map((f) => ({
                            key: f.key || "",
                            values: Array.isArray(f.values) ? f.values : [],
                        }));
                    }
                } catch (e) {
                    // Malformed/tampered query string — just ignore it, start with no filters.
                }
            }
        },
        // Keeps the URL in sync with the current filters — replace, not push, since this fires
        // on every filter change including the initial mount, and pushing would stack a
        // redundant history entry (same reasoning as RequestList.vue's identical pattern).
        // Going "back" from an application's detail page then lands on a URL that already
        // encodes whatever was selected, which is what restoreFiltersFromQuery() reads back.
        syncFiltersToQuery() {
            const query = { ...this.$route.query };
            const setOrDelete = (key, val) => {
                if (val) query[key] = String(val);
                else delete query[key];
            };
            setOrDelete("program_id", this.program_id);
            setOrDelete("district_id", this.district_id);
            setOrDelete("status_bucket", this.status_bucket);
            setOrDelete("question_filters", this.activeQuestionFiltersJson);
            this.$router.replace({ path: this.$route.path, query }).catch(() => {});
        },
        toggleBucket(bucket) {
            this.status_bucket = this.status_bucket === bucket ? "" : bucket;
            this.loadApplications();
        },
        onProgramChange() {
            this.questionFilters = [];
            this.priorityOpen = false;
            this.loadFilterableQuestions();
            this.loadPriorityQuestionOptions();
            this.loadPriorityQuestions();
            this.loadApplications();
        },
        loadPriorityQuestionOptions() {
            if (!this.program_id) {
                this.priorityQuestionOptions = [];
                return;
            }
            UserService.authPostRequest("get-sneha-sparsham-filterable-questions", {
                program_id: this.program_id,
                all: 1,
            }).then((res) => {
                this.priorityQuestionOptions = (res.data.status === "success" && res.data.data.questions) || [];
            }).catch(() => {
                this.priorityQuestionOptions = [];
            });
        },
        loadPriorityQuestions() {
            if (!this.program_id) {
                this.priorityQuestions = [];
                return;
            }
            UserService.authPostRequest("get-sneha-sparsham-priority-questions", {
                program_id: this.program_id,
            }).then((res) => {
                this.priorityQuestions = (res.data.status === "success" && res.data.data.keys) || [];
            }).catch(() => {
                this.priorityQuestions = [];
            });
        },
        togglePriorityQuestion(key) {
            const i = this.priorityQuestions.indexOf(key);
            if (i === -1) this.priorityQuestions.push(key);
            else this.priorityQuestions.splice(i, 1);
        },
        savePriorityQuestions() {
            UserService.authPostRequest("save-sneha-sparsham-priority-questions", {
                program_id: this.program_id,
                keys: this.priorityQuestions,
            }).then(() => {
                this.priorityOpen = false;
            });
        },
        loadFilterableQuestions() {
            if (!this.program_id) {
                this.filterableQuestions = [];
                this.questionFilters = [];
                return;
            }
            UserService.authPostRequest("get-sneha-sparsham-filterable-questions", {
                program_id: this.program_id,
            }).then((res) => {
                this.filterableQuestions = (res.data.status === "success" && res.data.data.questions) || [];
                // Drop any already-picked filter whose question doesn't exist for this programme.
                this.questionFilters = this.questionFilters.filter((f) =>
                    this.filterableQuestions.some((q) => q.key === f.key)
                );
            }).catch(() => {
                this.filterableQuestions = [];
            });
        },
        questionFilterOptionsFor(key) {
            const q = this.filterableQuestions.find((x) => x.key === key);
            return (q && q.options) || [];
        },
        isQuestionFilterUsed(key, idx) {
            return this.questionFilters.some((f, i) => i !== idx && f.key === key);
        },
        addQuestionFilter() {
            this.questionFilters.push({ key: "", values: [] });
        },
        removeQuestionFilter(idx) {
            const hadValues = !!(this.questionFilters[idx] && this.questionFilters[idx].key && this.questionFilters[idx].values.length);
            if (this.openAnswerIdx === idx) this.openAnswerIdx = null;
            this.questionFilters.splice(idx, 1);
            if (hadValues) this.loadApplications();
        },
        onQuestionFilterKeyChange(idx) {
            // Reset the answers — the previous selection almost certainly doesn't apply to the
            // newly-picked question (different option set entirely) — then jump straight into
            // its answer picker so the flow feels like one continuous step.
            this.questionFilters[idx].values = [];
            this.openAnswerIdx = idx;
        },
        toggleAnswerPicker(idx) {
            this.openAnswerIdx = this.openAnswerIdx === idx ? null : idx;
        },
        toggleFilterValue(idx, opt) {
            const values = this.questionFilters[idx].values;
            const i = values.indexOf(opt);
            if (i === -1) values.push(opt);
            else values.splice(i, 1);
        },
        clearQuestionFilters() {
            this.questionFilters = [];
            this.openAnswerIdx = null;
            this.loadApplications();
        },
        statusLabel(s) {
            return s === 'verified' ? 'Verified'
                : s === 'not_verified' ? 'Not Verified'
                : s === 'edit_requested' ? 'Edit Requested'
                : s === 'resubmitted' ? 'Resubmitted'
                : s === 'in_progress' ? 'In Progress' : 'Pending';
        },
        statusBadgeClass(s) {
            return s === 'verified' ? 'bg-success'
                : s === 'resubmitted' ? 'bg-info'
                : s === 'in_progress' ? 'bg-warning text-dark'
                : s === 'pending' || !s ? 'bg-secondary'
                : 'bg-danger';
        },
        percentClass(percent) {
            return percent >= 100 ? 'is-complete' : percent >= 50 ? 'is-partial' : 'is-low';
        },
        isAppExpanded(id) {
            return !!this.expandedApps[id];
        },
        toggleAppExpanded(id) {
            this.expandedApps = { ...this.expandedApps, [id]: !this.expandedApps[id] };
        },
        applicationDetailPath(applicationId) {
            return `/org/${this.$route.params.sub_level}/${this.$route.params.id}/sneha-sparsham-application/${applicationId}`;
        },
        loadApplications() {
            const levelId = parseInt(this.$route.params.id, 10);
            const userLevel = parseInt(this.$route.params.sub_level, 10);
            if (!userLevel) {
                this.applications = [];
                this.loading = false;
                return;
            }

            this.syncFiltersToQuery();
            this.loading = true;
            UserService.authPostRequest("get-sneha-sparsham-application-status-report", {
                user_level: userLevel,
                user_level_id: levelId,
                program_id: this.program_id || null,
                district_id: this.district_id || null,
                status_bucket: this.status_bucket || null,
                // ?assigned=1 → only the applications distributed to this approver for scoring.
                // Without it this screen lists every application in scope, approvers included.
                assigned_only: this.assignedOnly ? 1 : 0,
                question_filters: this.activeQuestionFiltersJson,
            }).then((res) => {
                if (res.data.status === "success") {
                    this.applications = res.data.data.list || [];
                    this.programs = res.data.data.programs || [];
                    this.districts = res.data.data.districts || [];
                    this.counts = res.data.data.counts || { completed: 0, going_on: 0, not_started: 0, vo_not_assigned: 0 };
                } else {
                    this.applications = [];
                }
            }).catch(() => {
                this.applications = [];
            }).finally(() => {
                this.loading = false;
            });
        },
        async exportExcel() {
            this.excelDownloading = true;
            try {
                const levelId = parseInt(this.$route.params.id, 10);
                const userLevel = parseInt(this.$route.params.sub_level, 10);
                const response = await UserService.authPostRequest("export-sneha-sparsham-application-status-report-excel", {
                    user_level: userLevel,
                    user_level_id: levelId,
                    program_id: this.program_id || null,
                    district_id: this.district_id || null,
                    status_bucket: this.status_bucket || null,
                    assigned_only: this.assignedOnly ? 1 : 0,
                    question_filters: this.activeQuestionFiltersJson,
                }, { responseType: "blob" });

                const blob = response.data;
                const contentType = (response.headers && response.headers["content-type"]) || "";
                if (contentType.indexOf("application/json") !== -1) {
                    const text = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.readAsText(blob);
                    });
                    const json = JSON.parse(text);
                    alert(json.message || "Download failed");
                    return;
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `sneha-sparsham-application-status-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            } catch (err) {
                let msg = "Failed to export Excel";
                if (err && err.response && err.response.data && typeof err.response.data === "object" && !(err.response.data instanceof Blob)) {
                    msg = err.response.data.message || msg;
                } else if (err && err.message) {
                    msg = err.message;
                }
                alert(msg);
            } finally {
                this.excelDownloading = false;
            }
        },
    },
};
</script>

<style scoped>
.sneha-page-note {
    margin: 0 0 14px;
    padding: 0 4px;
    font-size: 0.9rem;
    color: #6c757d;
    line-height: 1.45;
}

.sneha-empty-apps {
    text-align: center;
    padding: 24px 12px;
    color: #6c757d;
    font-size: 0.92rem;
    background: #fff;
    border-radius: 10px;
    border: 1px dashed #ced4da;
}

.sneha-app-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sneha-app-item {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.sneha-app-item.is-open {
    border-color: #c7d2f0;
}

.sneha-app-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    user-select: none;
}

.sneha-app-item:active .sneha-app-summary {
    background: #f8f9fa;
}

.sneha-app-main {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
}

.sneha-app-name {
    font-size: 0.92rem;
    font-weight: 600;
    color: #1a418c;
    line-height: 1.3;
    word-break: break-word;
}

.sneha-app-percent {
    flex-shrink: 0;
    font-size: 0.82rem;
    font-weight: 700;
}

.sneha-app-number {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: #6b7c93;
    background: #f4f7ff;
    border: 1px solid #dde4f5;
    border-radius: 5px;
    padding: 1px 6px;
    width: fit-content;
}

.sneha-app-summary-badges {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    margin-top: 1px;
}

.sneha-app-summary-badges .badge {
    font-size: 0.68rem;
    padding: 3px 7px;
}

.sneha-app-chevron {
    flex-shrink: 0;
    font-size: 18px;
    color: #adb5bd;
    transition: transform 0.15s ease;
}

.sneha-app-item.is-open .sneha-app-chevron {
    transform: rotate(180deg);
}

.sneha-app-details {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 0 12px 12px;
    border-top: 1px solid #eef0f5;
    padding-top: 10px;
    margin-top: -1px;
}

.sneha-app-view-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 8px;
    padding: 7px;
    border-radius: 8px;
    background: #1a418c;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    text-decoration: none;
}

.sneha-app-progress-track {
    height: 5px;
    background: #eef0f5;
    border-radius: 5px;
    overflow: hidden;
    margin: 2px 0 4px;
}

.sneha-app-progress-fill {
    height: 100%;
    border-radius: 5px;
}

.is-complete { color: #12a85a; }
.is-partial { color: #d9a441; }
.is-low { color: #d9534f; }
.sneha-app-progress-fill.is-complete { background: #1DCC70; }
.sneha-app-progress-fill.is-partial { background: #f0ad4e; }
.sneha-app-progress-fill.is-low { background: #d9534f; }

.sneha-app-program {
    font-size: 0.86rem;
    color: #6c757d;
    line-height: 1.35;
}

.sneha-app-meta {
    font-size: 0.82rem;
    color: #495057;
    line-height: 1.35;
}

.sneha-app-chevron {
    flex-shrink: 0;
    font-size: 20px;
    color: #adb5bd;
}

.sneha-filter-row {
    display: flex;
    gap: 8px;
}

.sneha-filter-select {
    flex: 1;
    min-width: 0;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%2364748b' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    border: 1.5px solid #dbe3ff;
    border-radius: 10px;
    padding: 10px 30px 10px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #27173E;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sneha-filter-select:focus {
    outline: none;
    border-color: #1a418c;
}

.sneha-qfilter-box {
    background: #f8f9fc;
    border: 1px solid #e3e7f5;
    border-radius: 12px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sneha-qfilter-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sneha-qfilter-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.sneha-qfilter-row .sneha-filter-select {
    padding: 8px 26px 8px 10px;
    font-size: 12px;
}

.sneha-qfilter-a-btn {
    width: 100%;
    padding: 8px 10px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    text-align: left;
    color: #1e2432;
}

.sneha-qfilter-a-btn ion-icon {
    flex-shrink: 0;
    font-size: 14px;
    color: #8792a8;
}

.sneha-qfilter-a-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sneha-qfilter-answers {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #fff;
    border: 1.5px solid #dbe3ff;
    border-radius: 10px;
    padding: 10px;
    max-height: 220px;
    overflow-y: auto;
}

.sneha-qfilter-answer-opt {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    margin: 0;
}

.sneha-qfilter-answer-opt input {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
}

.sneha-qfilter-answer-empty {
    font-size: 12px;
    color: #8792a8;
    margin: 0;
}

.sneha-qfilter-answer-done {
    align-self: flex-end;
    position: sticky;
    bottom: 0;
    margin-top: 4px;
    border: 1.5px solid #c7d2f0;
    border-radius: 8px;
    padding: 6px 16px;
    background: #eef2ff;
    color: #1a418c;
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 -6px 6px -6px rgba(20, 30, 60, 0.15);
}

.sneha-qfilter-actions {
    display: flex;
    gap: 6px;
}

.sneha-qfilter-clear-btn {
    flex: 1;
    border: 1.5px solid #dbe3ff;
    border-radius: 8px;
    padding: 7px;
    background: #fff;
    color: #a15c00;
    font-size: 12px;
    font-weight: 700;
}

.sneha-qfilter-apply-btn {
    flex: 1;
    border: none;
    border-radius: 8px;
    padding: 7px;
    background: #1a418c;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
}

.sneha-qfilter-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border: 1.5px solid #dbe3ff;
    border-radius: 10px;
    background: #fff;
    color: #a15c00;
    font-size: 18px;
}

.sneha-qfilter-add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1.5px dashed #1a418c;
    border-radius: 10px;
    padding: 8px;
    background: transparent;
    color: #1a418c;
    font-size: 12px;
    font-weight: 700;
}

.sneha-priority-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: none;
    border-radius: 8px;
    padding: 8px 10px;
    background: transparent;
    color: #1a418c;
    font-size: 13px;
    font-weight: 700;
}

.sneha-export-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 10px 12px;
    background: #12a85a;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.sneha-export-btn:disabled {
    opacity: 0.7;
}

.sneha-export-btn ion-icon {
    font-size: 16px;
}

.sneha-count-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
}

.sneha-count-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 3px;
    border-radius: 10px;
    border: 1.5px solid #eef0f5;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    user-select: none;
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
}

.sneha-count-tile:active {
    transform: scale(0.97);
}

.sneha-count-icon {
    font-size: 14px;
}

.sneha-count-num {
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.1;
}

.sneha-count-label {
    font-size: 0.6rem;
    font-weight: 700;
    color: #64748b;
    text-align: center;
    line-height: 1.2;
}

.is-completed .sneha-count-icon, .is-completed .sneha-count-num { color: #12a85a; }
.is-going-on .sneha-count-icon, .is-going-on .sneha-count-num { color: #d9a441; }
.is-not-started .sneha-count-icon, .is-not-started .sneha-count-num { color: #64748b; }
.is-vo-not-assigned .sneha-count-icon, .is-vo-not-assigned .sneha-count-num { color: #d9534f; }

.is-completed.is-active { border-color: #12a85a; background: #f4fdf8; }
.is-going-on.is-active { border-color: #d9a441; background: #fffaf1; }
.is-not-started.is-active { border-color: #94a3b8; background: #f8f9fa; }
.is-vo-not-assigned.is-active { border-color: #d9534f; background: #fdf4f4; }

.sneha-vo-box {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    background: #eef2ff;
    color: #1a418c;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.35;
}

.sneha-vo-box ion-icon {
    flex-shrink: 0;
    font-size: 15px;
}

.sneha-vo-box--none {
    background: #fdf4f4;
    color: #d9534f;
}

.sneha-interview-badge {
    background: #c26a00;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.sneha-interview-badge ion-icon {
    font-size: 13px;
}

.sneha-mark-badge {
    background: #1a418c;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.sneha-mark-badge ion-icon {
    font-size: 13px;
}
</style>
